import { Button, Grid, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import BlockData from '../../../models/BlockData';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import LinkButton from '../../Buttons/LinkButton';
import BlockButtonListData from './BlockButtonListData';
import CloseButton from '../../Buttons/Close';
import BlockComponentButton from '../../../models/BlockComponentValues/BlockComponentButton';
import GridWithSettings from '../../Layout/GridWithSettings';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';

export interface BlockButtonListProps extends BlockProps {
  blockData: BlockData[]
}

function BlockButtonList({
  data, isEditing, isTemplate, editHandler, blockData, showImageModal, onCloseImageModal, mainColor,
}: BlockButtonListProps) {
  const [props, setProps] = useState<BlockButtonListData>(data.data as BlockButtonListData);

  const handleAddButton = () => {
    props.buttonList.push(BlockComponentButton.getDefault());
    editHandler(data.id, props);
  };

  const buttonMenu = useMemo(() => {
    if (props.buttonList && props.buttonList.length > 0) {
      return props.buttonList.map((button, index) => (
        <Grid
          key={uuid()}
          minWidth={150}
          item
          xs={12}
          sm={6}
          md={3}
          sx={Utils.hoverBorder(isEditing)}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.buttonList.splice(index, 1);
              editHandler(data.id, props);
            }}
          />
          <LinkButton
            mainColor={mainColor}
            button={button}
            blockData={blockData}
            isEditable={isEditing && !isTemplate}
            isTemplate={isTemplate}
            updateHandler={(item) => {
              props.buttonList[index] = item;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      ));
    }
    return [];
  }, [blockData, data.id, editHandler, isEditing, isTemplate, props]);

  useEffect(() => {
    setProps(data.data as BlockButtonListData);
  }, [data.data]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Grid
      id={elementUuid}
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        overflow: 'hidden',
        p: 1.5,
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.url}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, url: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        <GridWithSettings
          settings={props.settings}
          isEditing={isEditing}
          direction="row"
          updateHandler={(settings) => {
            props.settings = settings;
            editHandler(data.id, props);
          }}
        >
          {buttonMenu}
        </GridWithSettings>
      </Grid>
      {isEditing && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
          <Button color="primary" onClick={handleAddButton}>
            <AddCircleRoundedIcon />
          </Button>
        </Stack>
      )}
    </Grid>
  );
}

export default BlockButtonList;
